/** Page de connexion admin — flux MFA en 2 étapes (mdp → code email). */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';

type Step = 'credentials' | 'code';

export function LoginPage() {
  const [step, setStep] = useState<Step>('credentials');

  // Étape 1
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // Étape 2
  const [challengeId, setChallengeId] = useState('');
  const [emailHint, setEmailHint] = useState('');
  const [code, setCode] = useState('');

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { requestCode, verifyCode } = useAuth();
  const navigate = useNavigate();

  const handleCredentialsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const challenge = await requestCode(username, password);
      setChallengeId(challenge.challenge_id);
      setEmailHint(challenge.email_hint);
      setStep('code');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Identifiants incorrects');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyCode(challengeId, code);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Code invalide');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep('credentials');
    setCode('');
    setError('');
  };

  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-ink">PointBreak</h1>
          <p className="text-sm text-stone-500 mt-1">Administration Pointage</p>
        </div>

        {step === 'credentials' ? (
          <form
            onSubmit={handleCredentialsSubmit}
            className="bg-white p-6 rounded-xl border border-stone-200 shadow-soft space-y-4"
          >
            <Input
              label="Identifiant"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="admin"
              autoFocus
              required
            />
            <Input
              label="Mot de passe"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
            />
            {error && <p className="text-xs text-red-600 text-center">{error}</p>}
            <Button type="submit" loading={loading} className="w-full">
              Continuer
            </Button>
            <div className="text-center">
              <Link
                to="/forgot-password"
                className="text-xs text-stone-500 hover:text-accent"
              >
                Mot de passe oublié ?
              </Link>
            </div>
          </form>
        ) : (
          <form
            onSubmit={handleCodeSubmit}
            className="bg-white p-6 rounded-xl border border-stone-200 shadow-soft space-y-4"
          >
            <div className="text-center">
              <div className="w-12 h-12 mx-auto rounded-full bg-accent-soft text-accent flex items-center justify-center mb-2">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="m22 7-10 5L2 7" />
                </svg>
              </div>
              <p className="text-sm text-stone-700">
                Un code à 6 chiffres a été envoyé à
              </p>
              <p className="text-sm font-mono font-semibold text-ink mt-0.5">{emailHint}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-stone-600 mb-1">Code de connexion</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="······"
                className="w-full text-center text-2xl tracking-[0.3em] font-bold rounded-lg border border-stone-300 px-3 py-3 focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                autoFocus
                required
              />
              <p className="text-[11px] text-stone-500 mt-1">Valide 10 minutes · 5 tentatives max.</p>
            </div>
            {error && <p className="text-xs text-red-600 text-center">{error}</p>}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={handleBack}
                className="flex-1"
              >
                Retour
              </Button>
              <Button
                type="submit"
                loading={loading}
                disabled={code.length !== 6}
                className="flex-1"
              >
                Valider
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
